import { useState } from "react";
import { Link } from "react-router-dom";
import { api, whatsappLink, PHONE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TRANSFORMATIONS } from "@/data/transformations";
import TransformationCard from "@/components/TransformationCard";
import { useLang } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import { ShieldCheck, BadgeCheck, HeartHandshake, Stethoscope, MessageCircle, Phone, ArrowRight, CheckCircle2, Star, PlayCircle } from "lucide-react";

const CFG = {
  implants: { slug: "implants", procedure: "Single Implant", us: "$4,000", ours: "$800", save: "77%", kw: ["implant"] },
  "all-on-4": { slug: "all-on-4", procedure: "All on 4", us: "$26,000", ours: "$8,500", save: "67%", kw: ["all-on", "full arch", "full mouth", "implant"] },
  "veneers-crowns": { slug: "veneers-crowns", procedure: "Veneers & Crowns", us: "$1,800", ours: "$400", save: "78%", kw: ["veneer", "crown", "makeover", "smile"] },
};

const DIFF_ICONS = [ShieldCheck, Stethoscope, BadgeCheck, HeartHandshake];
const TIMES = translations.en.ad.times; // canonical English values

const MOSAIC = (() => {
  const base = TRANSFORMATIONS.map((t) => t.image || t.after || t.before).filter(Boolean);
  return [...base, ...base];
})();

export default function AdLanding({ variant }) {
  const { t } = useLang();
  const cfg = CFG[variant] || CFG.implants;
  const v = t(`ad.variants.${cfg.slug}`);
  const differentiators = t("ad.differentiators");
  const timeLabels = t("ad.times");
  const [form, setForm] = useState({ name: "", email: "", phone: "", best_time: "" });
  const [status, setStatus] = useState("idle");

  const proof = (() => {
    const feat = TRANSFORMATIONS.filter((tr) => tr.featured);
    const matched = feat.filter((tr) => cfg.kw.some((k) => (tr.label || "").toLowerCase().includes(k)));
    return (matched.length ? matched : feat).slice(0, 3);
  })();

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await api.post("/leads", { ...form, procedure: cfg.procedure, best_contact: "", message: `Ad landing: /lp/${cfg.slug}` });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };
  const set = (k) => (val) => setForm((f) => ({ ...f, [k]: val?.target ? val.target.value : val }));

  return (
    <div data-testid="ad-landing">
      {/* HERO + inline form */}
      <section className="relative overflow-hidden bg-secondary text-white" data-testid="lp-hero">
        <div className="absolute inset-0 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6" aria-hidden="true" data-testid="lp-hero-mosaic">
          {MOSAIC.map((src, i) => (
            <img key={i} src={src} alt="" loading="lazy" className="w-full h-full object-cover aspect-square" />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-secondary via-secondary/95 to-secondary/75" />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, white 1px, transparent 1px)", backgroundSize: "22px 22px", opacity: 0.05 }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-bold uppercase tracking-wide">{v.eyebrow}</span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-[1.05] mt-4">{v.h1a} <span className="text-accent">{v.h1b}</span></h1>
            <p className="text-white/75 mt-4 max-w-md">{v.sub}</p>
            <div className="flex items-center gap-5 mt-6">
              <div className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /><span className="font-bold">4.9★</span><span className="text-white/60 text-sm">{t("ad.avg")}</span></div>
              <div className="w-px h-8 bg-white/20" />
              <div><span className="font-bold">16k+</span> <span className="text-white/60 text-sm">{t("ad.patientsGuided")}</span></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-7">
              {differentiators.map((d, i) => {
                const Icon = DIFF_ICONS[i];
                return (
                  <div key={i} className="flex items-start gap-2.5">
                    <Icon className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <div><p className="font-semibold text-sm leading-tight">{d.t}</p><p className="text-xs text-white/55 mt-0.5">{d.d}</p></div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lead form */}
          <div className="rounded-3xl bg-white text-foreground p-6 sm:p-8 shadow-2xl" data-testid="lp-lead-form">
            {status === "done" ? (
              <div className="text-center py-8" data-testid="lp-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                <h2 className="font-heading text-2xl font-bold mt-4">{t("ad.thankYou")}</h2>
                <p className="text-muted-foreground mt-2">{t("ad.thankYouSub")}</p>
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                  <Button className="rounded-full h-12 px-7 mt-5 bg-[#25D366] hover:bg-[#1eb457]" data-testid="lp-success-whatsapp"><MessageCircle className="w-4 h-4 mr-2" /> {t("common.whatsappUs")}</Button>
                </a>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1"><span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold px-2.5 py-1">{t("ad.freeReply2h")}</span></div>
                <h2 className="font-heading text-2xl font-bold">{t("ad.formTitle")}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t("ad.formSub")}</p>
                <form onSubmit={submit} className="space-y-3 mt-5">
                  <div><Label htmlFor="lp-name">{t("ad.fullName")}</Label><Input id="lp-name" required value={form.name} onChange={set("name")} className="h-12 mt-1" data-testid="lp-input-name" /></div>
                  <div><Label htmlFor="lp-email">{t("ad.email")}</Label><Input id="lp-email" type="email" required value={form.email} onChange={set("email")} className="h-12 mt-1" data-testid="lp-input-email" /></div>
                  <div><Label htmlFor="lp-phone">{t("ad.phoneWa")}</Label><Input id="lp-phone" required value={form.phone} onChange={set("phone")} className="h-12 mt-1" data-testid="lp-input-phone" /></div>
                  <div>
                    <Label>{t("ad.bestTime")}</Label>
                    <Select value={form.best_time} onValueChange={set("best_time")}>
                      <SelectTrigger className="h-12 mt-1" data-testid="lp-best-time"><SelectValue placeholder={t("ad.chooseTime")} /></SelectTrigger>
                      <SelectContent>{TIMES.map((tm, i) => <SelectItem key={tm} value={tm}>{timeLabels[i]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {status === "error" && <p className="text-sm text-red-600" data-testid="lp-error">{t("ad.error")}</p>}
                  <Button type="submit" disabled={status === "loading"} className="w-full rounded-full h-12 text-base" data-testid="lp-submit">
                    {status === "loading" ? t("common.sending") : <>{t("common.getMyFreeQuote")} <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                  <div className="flex items-center justify-center gap-4 pt-1">
                    <a href={`tel:${PHONE.replace(/[^+\d]/g, "")}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="lp-call"><Phone className="w-4 h-4" /> {PHONE}</a>
                    <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="lp-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Savings strip */}
      <section className="bg-white py-10" data-testid="lp-savings">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl border border-border p-6 sm:p-8 text-center shadow-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("ad.mathEyebrow")}</p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-4">
              <div><p className="text-sm text-muted-foreground">{t("ad.inUsa")} <span className="text-xs">({v.unit})</span></p><p className="font-heading text-3xl font-bold text-muted-foreground line-through decoration-red-400">{cfg.us}</p></div>
              <ArrowRight className="w-6 h-6 text-primary hidden sm:block" />
              <div><p className="text-sm text-foreground/70">{t("ad.withUsFrom")}</p><p className="font-heading text-4xl font-extrabold text-primary">{cfg.ours}</p></div>
              <span className="rounded-full bg-emerald-50 text-emerald-700 text-sm font-bold px-3 py-1.5">{t("ad.saveUpTo", { save: cfg.save })}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-4">{t("ad.mathFootnote")}</p>
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="bg-secondary/5 py-12" data-testid="lp-proof">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-xl mx-auto mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("ad.proofEyebrow")}</p>
            <h2 className="font-heading text-3xl font-bold mt-2">{t("ad.proofTitle")}</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {proof.map((item) => <TransformationCard key={item.id} item={item} className="!mb-0" />)}
          </div>
          <div className="text-center mt-8">
            <Link to="/video-testimonials"><Button variant="outline" className="rounded-full h-11 px-6 border-primary text-primary hover:bg-primary hover:text-white" data-testid="lp-video-link"><PlayCircle className="w-4 h-4 mr-2" /> {t("ad.watchVideos")}</Button></Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-secondary">{t("ad.finalTitle")}</h2>
          <p className="text-muted-foreground mt-3">{t("ad.finalSub")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <a href="#lp-hero" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="lp-input-name"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); document.querySelector('[data-testid="lp-input-name"]')?.focus(); }}>
              <Button className="rounded-full h-12 px-8" data-testid="lp-final-cta">{t("common.getMyFreeQuote")} <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </a>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full h-12 px-8 border-primary text-primary hover:bg-primary hover:text-white"><MessageCircle className="w-4 h-4 mr-2" /> {t("common.chatWhatsApp")}</Button>
            </a>
          </div>
          <Link to="/" className="inline-block text-sm text-muted-foreground hover:text-primary underline mt-8" data-testid="lp-fullsite-link">{t("ad.exploreFull")}</Link>
        </div>
      </section>
    </div>
  );
}
